import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useDashboardStatistics from '@/hooks/useDashboardStatistics';
import Skeleton from '@/components/shared/Skeleton';

// Імпорти іконок
import IconPaid from '@/img/icon32-paid.svg';
import IconDebt from '@/img/icon32-debt.svg';
import IconTasks from '@/img/icon32-tasks.svg';
import IconTenant from '@/img/icon32-tenant.svg';

import './DashboardHeaderSection.scss';

const baseClass = 'DashboardPage__DashboardHeaderSection';

interface DashboardHeaderSectionProps {
  className?: string;
}

interface StatCardProps {
  title: string;
  count: React.ReactNode;
  subtitleNumber: React.ReactNode;
  subtitleText: string;
  icon: React.ReactNode;
  iconBg: string;
  to: string;
}

// Helper для форматування валюти
const formatCurrency = (amount: number) => {
  return `$${amount.toLocaleString('en-US')}`;
};

const StatCard: React.FC<StatCardProps> = ({
                                             title,
                                             count,
                                             subtitleNumber,
                                             subtitleText,
                                             icon,
                                             iconBg,
                                             to,
                                           }) => (
  <Link to={to} className={`${baseClass}__card`}>
    <div className={`${baseClass}__card-top`}>
      <span className={`${baseClass}__card-title`}>{title}</span>
      <div
        className={`${baseClass}__card-icon`}
        style={{ backgroundColor: iconBg }}
      >
        {icon}
      </div>
    </div>
    <div className={`${baseClass}__card-count`}>{count}</div>
    <div className={`${baseClass}__card-bottom`}>
      <span className={`${baseClass}__card-subtitle-number`}>{subtitleNumber}</span>
      <span className={`${baseClass}__card-subtitle-text`}>{subtitleText}</span>
    </div>
  </Link>
);

const DashboardHeaderSection: React.FC<DashboardHeaderSectionProps> = ({
                                                                         className = '',
                                                                       }) => {
  const { data: dashboardData, isLoading } = useDashboardStatistics();

  const stats = useMemo(() => {
    if (!dashboardData?.success) {
      return {
        paidAmount: 0,
        overdueAmount: 0,
        pendingCount: 0,
        arrearsCount: 0,
        totalTenants: 0,
        activeTenants: 0,
        totalTasks: 0,
        tasksInProgress: 0,
      };
    }

    const overview = dashboardData.data.overview;
    return {
      paidAmount: overview.paidRents.amount,
      overdueAmount: overview.arrears.amount,
      pendingCount: overview.paidRents.pendingCount,
      arrearsCount: overview.arrears.overdueCount,
      totalTenants: overview.tenants.total,
      activeTenants: overview.tenants.registeredOnPlatform,
      totalTasks: overview.tasks.total,
      tasksInProgress: overview.tasks.inProgress,
    };
  }, [dashboardData]);

  const containerClasses = [baseClass, className].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <div className={`${baseClass}__info`}>
        <span className={`${baseClass}__title`}>Dashboard / Property owner</span>
        <Link to="/dashboard/properties/add" className={`${baseClass}__add-btn`}>
          Add new property
        </Link>
      </div>

      <div className={`${baseClass}__cards`}>
        <StatCard
          title="Paid rents"
          count={
            isLoading ? (
              <Skeleton isLoading={true} style={{ width: '120px' }} />
            ) : (
              formatCurrency(stats.paidAmount)
            )
          }
          subtitleNumber={isLoading ? '...' : stats.pendingCount}
          subtitleText="payments pending"
          to="/dashboard/payments"
          iconBg="rgba(118, 193, 2, 0.2)"
          icon={<img src={IconPaid} alt="Paid rents" width={24} height={24} />}
        />

        <StatCard
          title="Arrears"
          count={
            isLoading ? (
              <Skeleton isLoading={true} style={{ width: '120px' }} />
            ) : (
              formatCurrency(stats.overdueAmount)
            )
          }
          subtitleNumber={isLoading ? '...' : stats.arrearsCount}
          subtitleText="overdue payments"
          to="/dashboard/tenants#debtors-section"
          iconBg="rgba(251, 47, 84, 0.2)"
          icon={<img src={IconDebt} alt="Arrears" width={24} height={24} />}
        />

        <StatCard
          title="Tasks"
          count={
            isLoading ? (
              <Skeleton isLoading={true} style={{ width: '80px' }} />
            ) : (
              stats.totalTasks
            )
          }
          subtitleNumber={isLoading ? '...' : stats.tasksInProgress}
          subtitleText="in progress"
          to="/dashboard/calendar#tasks-calendar-section"
          iconBg="rgba(92, 178, 239, 0.2)"
          icon={<img src={IconTasks} alt="Tasks" width={24} height={24} />}
        />

        <StatCard
          title="Tenants"
          count={
            isLoading ? (
              <Skeleton isLoading={true} style={{ width: '80px' }} />
            ) : (
              stats.totalTenants
            )
          }
          subtitleNumber={isLoading ? '...' : stats.activeTenants}
          subtitleText="registered on PropSavvy"
          to="/dashboard/tenants"
          iconBg="rgba(255, 193, 7, 0.2)"
          icon={<img src={IconTenant} alt="Tenants" width={24} height={24} />}
        />
      </div>
    </div>
  );
};

export default DashboardHeaderSection;